import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { OAuthLogin } from "@/components/OAuthLogin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { checkEmailExists } from "@/pages/user/user.service";
import { Helmet } from "react-helmet-async";

export function Login() {
  const [, setLocation] = useLocation();
  const { login, isAuthenticated, resetPassword } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Login form state
  const [showTraditionalLogin, setShowTraditionalLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");

  // Forgot password modal state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState<"email" | "reset">("email");
  const [fpError, setFpError] = useState("");

  // OAuth redirect check
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthResult = urlParams.get("oauth");
    const oauthError = urlParams.get("error");

    if (oauthResult === "success") {
      toast({ title: "Login Successful", description: "You have been logged in successfully!", variant: "default" });
      setLocation("/");
    }

    if (oauthError === "oauth") {
      toast({ title: "Login Failed", description: "OAuth login failed. Please try again.", variant: "destructive" });
    }
  }, [toast, setLocation]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) setLocation("/");
  }, [isAuthenticated, setLocation]);

  // Handle login input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  // Handle traditional login
  const handleTraditionalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.email || !formData.password) { setError("Please fill in all fields"); return; }

    try {
      setIsSubmitting(true);
      await login(formData.email, formData.password);
      toast({ title: "Login Successful", description: "Welcome back to GCEP!", variant: "default" });
      setLocation("/");
    } catch (err: any) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle forgot password
  const handleForgotPassword = async (e: React.FormEvent) => {
  e.preventDefault();
  setFpError("");

    if (step === "email") {
      if (!forgotEmail) {
        setFpError("Please enter your email");
        return;
      }

      try {
        const exists = await checkEmailExists(forgotEmail);
        if (!exists) {
          setFpError("Email not found");
          return;
        }
        setStep("reset");
      } catch (err: any) {
        setFpError(err.message || "Failed to verify email");
      }

    } else {
      if (!newPassword || !confirmPassword) {
        setFpError("Please fill in all fields");
        return;
      }

      if (newPassword !== confirmPassword) {
        setFpError("Passwords do not match");
        return;
      }

      try {
        await resetPassword(forgotEmail, newPassword);
        toast({
          title: "Password Reset Successful",
          description: "You can now login with your new password.",
          variant: "default",
        });

        setShowForgotPassword(false);
        setStep("email");
        setForgotEmail("");
        setNewPassword("");
        setConfirmPassword("");
        setFpError("");
      } catch (err: any) {
        setFpError(err.message || "Failed to reset password");
      }
    }
  };

  return (
    <>
      {/* Meta tags for SEO */}
      <Helmet>
        <title>Sign In - GCEP</title>
        <meta name="description" content="Sign in to Gujarat Competitive Exam Preparation portal to access exam resources and practice tests." />
        <meta name="keywords" content="GCEP, login, competitive exams, Gujarat, exam preparation" />
        <meta name="author" content="GCEP Team" />
        <meta property="og:title" content="GCEP - Sign In" />
        <meta property="og:description" content="Sign in to Gujarat Competitive Exam Preparation portal to access exam resources and practice tests." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ---------- OAuth screen ---------- */}
      {!showTraditionalLogin ? (
        <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 p-4">
          <div className="w-full max-w-md text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Gujarat Competitive Exam Preparation</h1>
            <p className="text-gray-600 dark:text-gray-300 mb-8">Your gateway to exam success</p>
            <OAuthLogin
              title="Sign in to GCEP"
              description="Choose your preferred sign-in method"
              showTraditionalLogin={true}
              onTraditionalLogin={() => setShowTraditionalLogin(true)}
            />
            <Button type="button" variant="outline" className="w-full mt-4" onClick={() => setLocation("/")}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <p className="mt-6 text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{" "}
              <button onClick={() => setLocation("/register")} className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
                Sign up here
              </button>
            </p>
          </div>
        </div>
      ) : (
        /* ---------- Email/Password screen ---------- */
        <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 p-4">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Sign in with Email</CardTitle>
                <CardDescription>Enter your email and password to continue</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleTraditionalLogin} className="space-y-4">
                  {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input name="email" type="email" placeholder="Enter your email" value={formData.email} onChange={handleInputChange} required />
                  </div>
                  <div className="space-y-2 relative">
                    <Label>Password</Label>
                    <Input name="password" type={showPassword ? "text" : "password"} placeholder="Enter your password" value={formData.password} onChange={handleInputChange} required />
                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-2 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                    </Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Sign In"}
                    </Button>
                  </div>
                  <div className="text-right">
                    <button type="button" className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400" onClick={() => setShowForgotPassword(true)}>
                      Forgot Password?
                    </button>
                  </div>
                  <Button type="button" variant="outline" className="w-full" onClick={() => setShowTraditionalLogin(false)}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                </form>
                <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                  Don't have an account?{" "}
                  <button onClick={() => setLocation("/register")} className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
                    Sign up here
                  </button>
                </p>
              </CardContent>
            </Card>

            {/* ---------- Forgot Password Modal ---------- */}
            <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Forgot Password</DialogTitle>
                  <DialogDescription>
                    {step === "email" ? "Enter your registered email" : "Enter your new password"}
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleForgotPassword} className="space-y-4 mt-2">
                  {fpError && <Alert variant="destructive"><AlertDescription>{fpError}</AlertDescription></Alert>}

                  {step === "email" ? (
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>New Password</Label>
                        <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Confirm Password</Label>
                        <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center">
                    <Button type="submit">{step === "email" ? "Verify" : "Reset Password"}</Button>
                    <Button type="button" variant="outline" onClick={() => {
                      setShowForgotPassword(false);
                      setStep("email");
                      setForgotEmail("");
                      setNewPassword("");
                      setConfirmPassword("");
                      setFpError("");
                    }}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      )}
    </>
  );
}
